import React, { useEffect, useState } from "react";
import { Loader2, Upload, Save, CheckCircle2 } from "lucide-react";
import { apiGetSettings, apiUpdateSettings, apiUpload, resolveImg } from "../../api";

const AdminSettings = () => {
  const [form, setForm] = useState({ upiId: "", upiPayeeName: "", upiQrImage: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    apiGetSettings()
      .then(setForm)
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setSaved(false); };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await apiUpload(file);
      set("upiQrImage", url);
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiUpdateSettings(form);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-neutral-500">Loading...</div>;
  }

  return (
    <div>
      <h1 className="font-serif text-3xl text-neutral-900 mb-2">Payment Settings</h1>
      <p className="text-sm text-neutral-500 mb-6">
        Set your UPI ID and QR code. Customers will scan this to pay directly — no payment gateway needed.
        Verify payment manually in your UPI app, then mark the order Confirmed to dispatch it.
      </p>

      <div className="bg-white rounded-sm border border-neutral-200 p-6 max-w-xl space-y-4">
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">UPI ID</label>
          <input
            value={form.upiId}
            onChange={(e) => set("upiId", e.target.value)}
            placeholder="yourname@upi"
            className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#C8932F]"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Payee Name (shown to customer)</label>
          <input
            value={form.upiPayeeName}
            onChange={(e) => set("upiPayeeName", e.target.value)}
            placeholder="Deegford Fine Fragrances"
            className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#C8932F]"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">UPI QR Code Image</label>
          <div className="flex gap-2">
            <input
              value={form.upiQrImage}
              onChange={(e) => set("upiQrImage", e.target.value)}
              placeholder="https://... or upload"
              className="flex-1 border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#C8932F]"
            />
            <label className="flex items-center gap-1.5 border border-neutral-300 rounded-sm px-3 text-sm cursor-pointer hover:bg-neutral-50">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              <input type="file" accept="image/*" className="hidden" onChange={handleUpload} />
            </label>
          </div>
          {form.upiQrImage && (
            <img src={resolveImg(form.upiQrImage)} alt="UPI QR" className="mt-3 h-40 w-40 object-contain border border-neutral-200 rounded-sm p-2" />
          )}
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 bg-gradient-to-br from-[#FFE29A] to-[#C8932F] text-[#050304] px-5 py-2.5 text-sm font-semibold uppercase tracking-widest rounded-sm hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </button>
          {saved && (
            <span className="flex items-center gap-1.5 text-sm text-green-600">
              <CheckCircle2 className="h-4 w-4" /> Saved
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
